import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { QRCodeSVG } from "qrcode.react";
import AddIcon from "@mui/icons-material/Add";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { getRightworkAPI, postRightworkAPI } from "../../requests/utilities/requests";
import {
  API_get_maintenance_asset,
  API_get_maintenance_requests,
  API_archive_maintenance_asset,
} from "../../../../constants";
import LoadSpinner from "../../../../utilities/LoadSpinner";
import { objectToCSV, downloadCSV } from "../../../../utilities/csvExport";
import { StatusBadge } from "../MaintenanceBadges";
import { assetStickerLink } from "./AssetLabelSheet";
import { ageSince, formatMoney, formatMonthYear, isWarrantyExpired } from "./assetFormat";

function StatTile({ label, value }) {
  return (
    <div className="border border-slate-200 rounded-[10px] px-4 py-3 bg-white">
      <p className="text-xs font-medium text-slate-500">{label}</p>
      <p className="mt-1 text-xl font-bold text-slate-900">{value}</p>
    </div>
  );
}

function partsSummary(parts) {
  return (parts || []).map((p) => `${p.name}${p.quantity > 1 ? ` ×${p.quantity}` : ""}`).join(", ");
}

// One asset: its details, sticker, rollups, and every ticket filed on it.
function AssetDetailView({
  assetUuid,
  homeStore,
  storeId,
  storeName,
  onBack,
  onEdit,
  onPrintLabel,
  onNewTicket,
  onOpenTicket,
  onChanged,
  refreshKey,
}) {
  const [asset, setAsset] = useState(null);
  const [canEdit, setCanEdit] = useState(false);
  const [tickets, setTickets] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [assetRes, ticketRes] = await Promise.all([
        getRightworkAPI(API_get_maintenance_asset, {
          store_id: homeStore,
          asset_store_id: storeId,
          asset_uuid: assetUuid,
        }),
        getRightworkAPI(API_get_maintenance_requests, {
          store_id: homeStore,
          category: "maintenance",
          asset_uuid: assetUuid,
        }),
      ]);
      setAsset(assetRes.data?.redux?.payload?.maintenance_asset || null);
      setCanEdit(Boolean(assetRes.data?.redux?.payload?.can_edit));
      setTickets(ticketRes.data?.redux?.payload?.maintenance_requests || []);
    } catch (error) {
      toast.error("Failed to load asset");
    } finally {
      setIsLoading(false);
    }
  }, [assetUuid, homeStore, storeId]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  const handleArchive = async () => {
    const archiving = !asset.is_archived;
    if (
      archiving &&
      !window.confirm(
        `Archive ${asset.name}? Its sticker stops working and it leaves the picker. Its ticket history stays.`
      )
    ) {
      return;
    }
    try {
      const res = await postRightworkAPI(API_archive_maintenance_asset, {
        store_id: homeStore,
        asset_store_id: storeId,
        asset_uuid: asset.uuid,
        archived: archiving,
      });
      setAsset((prev) => ({ ...prev, ...res.data?.redux?.payload?.maintenance_asset }));
      toast.success(archiving ? "Asset archived" : "Asset restored");
      onChanged?.();
    } catch (error) {
      toast.error(error.response?.data?.system?.message || "Failed to update asset");
    }
  };

  const handleExport = () => {
    const rows = tickets.map((t) => ({
      Ticket: t.ticket_number != null ? `#${t.ticket_number}` : "",
      Title: t.title,
      Status: t.status,
      Reported: t.date_reported || "",
      Resolved: t.date_resolved || "",
      Vendor: t.vendor || "",
      "Repair cost": t.repair_cost || "",
      Parts: partsSummary(t.parts_used),
      Description: t.description || "",
    }));
    if (rows.length === 0) {
      toast.info("No tickets to export yet");
      return;
    }
    // Asset identity first (what a warranty desk asks for), then the tickets.
    // Both go through objectToCSV so user text is quoted and formula-safe.
    const header = objectToCSV([
      { Field: "Asset", Value: `${asset.name} (${asset.code})` },
      { Field: "Manufacturer", Value: asset.manufacturer || "" },
      { Field: "Model", Value: asset.model_number || "" },
      { Field: "Serial", Value: asset.serial_number || "" },
      { Field: "Installed", Value: asset.install_date || "" },
      { Field: "Warranty until", Value: asset.warranty_expires || "" },
    ]);
    const fileCode = (asset.code || "asset").replace(/[^A-Za-z0-9_-]/g, "_");
    downloadCSV(`${header}\n\n${objectToCSV(rows)}`, `${fileCode}_repair_history.csv`);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-40">
        <LoadSpinner />
      </div>
    );
  }
  if (!asset) {
    return (
      <div className="py-20 text-center text-slate-500">
        <p>This asset couldn't be loaded.</p>
        <button type="button" className="mt-3 text-violet-700 underline" onClick={onBack}>
          Back to assets
        </button>
      </div>
    );
  }

  const stats = asset.stats || {};
  const topPart = stats.top_parts?.[0];
  const warrantyExpired = isWarrantyExpired(asset.warranty_expires);
  const details = [
    ["Make", asset.manufacturer],
    ["Model", asset.model_number],
    ["Serial", asset.serial_number],
    ["Installed", asset.install_date && `${formatMonthYear(asset.install_date)} (${ageSince(asset.install_date)})`],
    ["Purchase cost", formatMoney(asset.purchase_cost)],
    [
      "Warranty",
      asset.warranty_expires &&
        (warrantyExpired
          ? `Expired ${formatMonthYear(asset.warranty_expires)}`
          : `Until ${formatMonthYear(asset.warranty_expires)}`),
    ],
    ["Service vendor", asset.vendor],
    ["Notes", asset.notes],
  ];

  return (
    <div className="flex flex-col gap-5">
      <button
        type="button"
        onClick={onBack}
        className="self-start flex items-center gap-1 text-sm font-medium text-violet-700 hover:text-violet-800"
      >
        <ArrowBackIcon sx={{ fontSize: 18 }} />
        Assets
      </button>

      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-3 flex-wrap">
            <h2 className="text-2xl font-bold text-slate-900">{asset.name}</h2>
            {stats.repeat_issue && (
              <span className="px-2.5 py-0.5 rounded-full border border-amber-200 bg-amber-50 text-amber-800 text-xs font-semibold">
                Repeat issue
              </span>
            )}
            {asset.is_archived && (
              <span className="px-2.5 py-0.5 rounded-full border border-slate-200 bg-slate-100 text-slate-600 text-xs font-semibold">
                Archived
              </span>
            )}
          </div>
          <p className="mt-1 text-sm text-slate-500">
            {[asset.code, asset.location, storeName].filter(Boolean).join(" · ")}
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {canEdit && (
            <>
              <button
                type="button"
                onClick={handleArchive}
                className="px-4 py-2.5 rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50 text-sm font-medium"
              >
                {asset.is_archived ? "Restore" : "Archive"}
              </button>
              <button
                type="button"
                onClick={() => onEdit(asset)}
                className="px-4 py-2.5 rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50 text-sm font-medium"
              >
                Edit
              </button>
            </>
          )}
          {!asset.is_archived && (
            <button
              type="button"
              onClick={() => onNewTicket(asset)}
              className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-violet-700 hover:bg-violet-800 text-white text-sm font-medium"
            >
              <AddIcon fontSize="small" />
              New ticket for this asset
            </button>
          )}
        </div>
      </div>

      <div className="flex gap-6 flex-wrap items-start">
        <div className="flex flex-col gap-4" style={{ flex: "1 1 320px", maxWidth: 380 }}>
          <section className="border border-slate-200 rounded-[10px] p-5 bg-white">
            <h3 className="text-sm font-semibold text-slate-900">Details</h3>
            <dl className="mt-3 grid gap-x-3 gap-y-2.5 text-[13px]" style={{ gridTemplateColumns: "110px 1fr" }}>
              {details.map(([label, value]) => (
                <React.Fragment key={label}>
                  <dt className="text-slate-500">{label}</dt>
                  <dd
                    className={`m-0 ${
                      label === "Warranty" && warrantyExpired ? "text-red-700" : "text-slate-900"
                    } whitespace-pre-wrap break-words`}
                  >
                    {value || <span className="text-slate-300">—</span>}
                  </dd>
                </React.Fragment>
              ))}
            </dl>
          </section>

          {!asset.is_archived && (
            <section className="border border-slate-200 rounded-[10px] p-5 bg-white flex gap-4 items-center">
              <QRCodeSVG value={assetStickerLink(asset)} size={96} level="M" />
              <div className="min-w-0">
                <h3 className="text-sm font-semibold text-slate-900">Sticker</h3>
                <p className="mt-1 text-xs text-slate-500 leading-relaxed">
                  Stick this on the equipment. Typing{" "}
                  <span className="font-semibold text-slate-900">{asset.code}</span> in a ticket's asset search
                  picks it.
                </p>
                <button
                  type="button"
                  onClick={() => onPrintLabel(asset)}
                  className="mt-2 text-sm font-medium text-violet-700 hover:text-violet-800"
                >
                  Print label
                </button>
              </div>
            </section>
          )}
        </div>

        <div className="flex flex-col gap-4 min-w-0" style={{ flex: "999 1 520px" }}>
          <div className="grid gap-3" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))" }}>
            <StatTile label="Tickets (12 mo)" value={stats.tickets_12mo ?? 0} />
            <StatTile label="Repair spend (12 mo)" value={formatMoney(stats.spend_12mo) || "$0"} />
            <StatTile label="Avg. days to fix" value={stats.avg_days_to_fix ?? "—"} />
            <StatTile
              label="Most replaced part"
              value={topPart ? `${topPart.name}${topPart.quantity > 1 ? ` (×${topPart.quantity})` : ""}` : "—"}
            />
          </div>

          <section className="border border-slate-200 rounded-[10px] bg-white overflow-hidden">
            <div className="flex items-center justify-between px-5 py-3.5 border-b border-slate-200">
              <h3 className="text-sm font-semibold text-slate-900">Ticket history</h3>
              <button
                type="button"
                onClick={handleExport}
                className="text-sm font-medium text-violet-700 hover:text-violet-800"
              >
                Export for warranty claim
              </button>
            </div>
            {tickets.length === 0 ? (
              <p className="px-5 py-8 text-sm text-slate-400 text-center">No tickets filed on this asset yet.</p>
            ) : (
              tickets.map((t) => (
                <button
                  type="button"
                  key={t.uuid}
                  onClick={() => onOpenTicket(t)}
                  className="w-full text-left flex items-start gap-4 px-5 py-3.5 border-b border-slate-100 hover:bg-slate-50 flex-wrap"
                >
                  <span className="w-20 flex-shrink-0 text-xs text-slate-500 pt-0.5 tabular-nums">
                    {t.date_reported}
                  </span>
                  <span className="flex-1 min-w-0" style={{ flexBasis: 220 }}>
                    <span className="flex items-center gap-2">
                      <span className="text-xs text-slate-500 tabular-nums">
                        {t.ticket_number != null ? `#${t.ticket_number}` : ""}
                      </span>
                      <span className="text-[13px] font-semibold text-slate-900">{t.title}</span>
                    </span>
                    {(t.parts_used?.length > 0 || t.vendor) && (
                      <span className="block text-xs text-slate-500 mt-0.5">
                        {[partsSummary(t.parts_used), t.vendor].filter(Boolean).join(" · ")}
                      </span>
                    )}
                  </span>
                  <StatusBadge status={t.status} approvalStatus={t.approval_status} />
                  <span className="w-20 text-right text-[13px] font-semibold text-slate-900 tabular-nums">
                    {formatMoney(t.repair_cost, { cents: true }) || <span className="text-slate-300">—</span>}
                  </span>
                </button>
              ))
            )}
          </section>
        </div>
      </div>
    </div>
  );
}

export default AssetDetailView;
